import React from 'react';
import { createId } from '@paralleldrive/cuid2';
import { isEmpty, isNil, noop } from 'lodash/fp';
import StarRatings from 'react-star-ratings';
import { Tooltip } from '@material-ui/core';
import { default as styles, starDimension, starSpacing } from './StandardCard.styles';
import {
  IMGWIDTH,
  TOPHEIGHT,
  COLHEIGHT,
  COLWIDTH,
  HORPADDING,
  BORDERCLASS,
  BOTTOMHEIGHT,
  BORDERWIDTH,
} from './StandardCard.styles';
import { ContainerPayload } from 'src/components/RightContainer/RightContainer.slice';
import { RightContainerPayloadType } from 'src/components/RightContainer/RightContainer';
import { WorklistType } from 'src/components/WorklistIcon/WorklistIcon';
import { starGoldColor } from 'src/utils/Style/Theme';
import { IS_HINDSIGHTING } from 'src/utils/Domain/ConstantsFunctions';
import Renderer, { type RendererOptions } from 'src/utils/Domain/Renderer';
import { decorateTextByClasses } from 'src/style/styles.utils';
import { CardViewCardColumn, CardViewItem } from '../UIData.types';
import CenteredImage from 'src/components/CenteredImage/CenteredImage';
import { classes } from 'typestyle';
import { AdornmentType } from 'configuration-schemas/viewdefns/literals';
import Adornments, { AdornmentContextOptions } from 'src/components/Adornments/Adornments';
import { ContextMenuType } from 'src/components/WorklistContextMenu/WorklistContextMenu';
import { ConfigurableGridButton } from 'configuration-schemas/viewdefns/general';
import { TemplateRendererParams } from 'src/components/ConfigurableGrid/ConfigurableGrid.types';

export interface StandardCardProps {
  popoverTitle?: string;
  imgSrc: string;
  stars: number;
  id: string;
  name: string;
  // Only used by CollectionCard atm
  subname?: string;
  collectionType?: string;
  slotName?: string;
  children?: StandardCardProps[];
  /** Added to support some limited buttons in `CollectionCard`, only implemented there */
  buttons?: ConfigurableGridButton[];
  // End of CollectionCard
  styleName: string;
  styleId: string;
  styleColorName?: string;
  leafId?: string;
  description: string;
  styleDescription: string;
  value: number | string;
  valueRenderer: string;
  /** Params passed to the `valueRenderer`. Only the `mask`/`maskParams` properties are currently implemented. */
  valueRendererParams?: TemplateRendererParams;
  nameRenderer?: string;
  nameRendererParams?: TemplateRendererParams;
  descriptionRenderer?: string;
  descriptionRendererParams?: TemplateRendererParams;
  columns?: CardViewCardColumn[];
  departmentId?: string;
  swatchIds?: string[];
  onItemClick?: (item: ContainerPayload, eventTarget?: HTMLElement) => void;
  onMoreInfoClick?: (item: ContainerPayload) => void;
  isSelected?: boolean;
  /**
   * When true, does not style the card with a checkmark when selected (i.e. StandardCard in add to cart views).
   *
   * Allowing the parent to provide a custom style to the selected card.
   */
  ignoreSelectionStyles?: boolean;
  stylePaneKey?: string;
  adornments: AdornmentType[];
  allowWorklistFunctionality?: boolean;
  // FIXME: only here for planogram use

  worklist_type?: WorklistType;
  /** Setting to true renders a card (with no rendered image/hover styles) to fill in blank space in the cards Grid.
   *  This prevents a potential runtime error during card props lookup in ColumnGroupedView component */
  isBlankCard?: boolean;
  isPrintMode?: boolean;
  /** Added for worklist selection and adding */
  selectable?: boolean;
  selectionId?: string;
  onSelectItem?: (item: CardViewItem) => void;
}

interface State {
  popoverOpen: boolean;
  loadingImages: boolean;
  targetId: string;
}

/** clips content via className (clipText/clampText), shows a tooltip only if it's actually truncated */
const ClippedTooltip: React.FC<{ content: React.ReactNode; className?: string }> = ({ content, className }) => {
  const ref = React.useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = React.useState(false);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (el) {
      setIsOverflowing(el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight);
    }
  }, [content]);

  const span = (
    <span ref={ref} className={className}>
      {content}
    </span>
  );

  return isOverflowing ? <Tooltip title={content ?? ''}>{span}</Tooltip> : span;
};

export class StandardCard extends React.Component<StandardCardProps, State> {
  static calcCardWidth = (colItemCount: number) => {
    // This increases the card with, should you have more than 8 columns of metrics
    const widthAddOn = colItemCount > 48 ? 31 : 10;
    // Add .01 so if the number of items is 1 over the total items per col, it will create the extra column
    return (
      IMGWIDTH +
      widthAddOn +
      (Math.ceil(colItemCount / Math.ceil(TOPHEIGHT / COLHEIGHT) + 0.01) || 1) * COLWIDTH +
      HORPADDING
    );
  };

  static calcCardHeight = () => {
    return TOPHEIGHT + BOTTOMHEIGHT + BORDERWIDTH * 2;
  };

  constructor(props: StandardCardProps) {
    super(props);
    this.state = {
      popoverOpen: false,
      loadingImages: false,
      targetId: createId(),
    };
  }

  getContainerPayload = (): ContainerPayload => {
    const { stylePaneKey, ...cardProps } = this.props;
    const isHindsighting = IS_HINDSIGHTING(stylePaneKey);
    const containerPayload: ContainerPayload = {
      type: isHindsighting ? RightContainerPayloadType.History : RightContainerPayloadType.Assortment,
      id: cardProps.leafId || cardProps.id,
      parentId: cardProps.styleId,
      isAssortmentBuild: !isHindsighting,
    };
    return containerPayload;
  };

  getContextOptions = (): AdornmentContextOptions => {
    const { onItemClick } = this.props;
    const containerPayload = this.getContainerPayload();
    const openStylePane = !isNil(onItemClick) ? onItemClick.bind(null, containerPayload) : noop;
    const contextOptions: AdornmentContextOptions = {
      type: ContextMenuType.card,
      parentIdentityField: 'styleId',
      onOpenStylePane: openStylePane,
      nodeData: this.props,
    };
    return contextOptions;
  };

  handleClick = () => {
    const { selectable, onSelectItem } = this.props;
    // for selectable worklist views
    if (selectable && onSelectItem) {
      onSelectItem(this.props as any);
      return;
    }
    const { allowWorklistFunctionality = false, onItemClick } = this.props;
    if (onItemClick && !allowWorklistFunctionality) {
      const containerPayload = this.getContainerPayload();
      onItemClick(containerPayload);
    }
  };

  render() {
    const {
      imgSrc,
      description,
      columns,
      stars,
      value,
      valueRenderer,
      valueRendererParams,
      nameRenderer,
      nameRendererParams,
      descriptionRenderer,
      descriptionRendererParams,
      id,
      name,
      isSelected = false,
      adornments,
      isBlankCard,
      ignoreSelectionStyles = false,
    } = this.props;
    const renderAdornments = !isEmpty(adornments);
    const contextOptions = this.getContextOptions();
    const cardColumns = columns
      ? columns.map((column, index) => {
          const rendererFn = Renderer[column.renderer as RendererOptions];
          // @ts-expect-error
          const renderedValue = rendererFn ? rendererFn(column.value, column.rendererParams?.mask) : column.value;

          const decoratedValue = decorateTextByClasses(renderedValue as React.ReactNode, column.classes ?? []);
          return (
            <div key={index} className={styles.colItem} data-qa={column.title.toLocaleLowerCase()}>
              <div className={BORDERCLASS} />
              {column.title}
              <br />
              {decoratedValue}
            </div>
          );
        })
      : [];

    const rendererFn = Renderer[valueRenderer as RendererOptions];
    // @ts-expect-error
    const renderedValue = rendererFn ? rendererFn(value, valueRendererParams?.mask) : value;

    const nameRendererFn = Renderer[nameRenderer as RendererOptions];
    // @ts-expect-error
    const renderedName = nameRendererFn ? nameRendererFn(name, nameRendererParams?.mask) : name;

    const descriptionRendererFn = Renderer[descriptionRenderer as RendererOptions];
    let renderedDescription = description;
    if (descriptionRendererFn) {
      // @ts-expect-error
      renderedDescription = descriptionRendererFn(description, descriptionRendererParams?.mask);
    }

    const customHoverStyle = isSelected ? '' : styles.getHoverStyle(isBlankCard || false);
    const customSelectionStyle = ignoreSelectionStyles ? styles.getSelectionStyle(isSelected) : '';

    return (
      <div
        className={classes(styles.card, customHoverStyle, customSelectionStyle)}
        style={{
          width: StandardCard.calcCardWidth(cardColumns.length),
        }}
        data-qa-component="StandardCard"
        data-qa-key={id}
        onClick={this.handleClick}
      >
        {renderAdornments && <Adornments adornments={adornments} productId={id} contextOptions={contextOptions} />}
        {!ignoreSelectionStyles && isSelected && (
          <React.Fragment>
            <div className={styles.cardOverlay} />
            <i className={`fas fa-check-circle ${styles.iconOverlay}`} />
          </React.Fragment>
        )}
        <div id={this.state.targetId} data-qa-action="CardClick" className={styles.infoContainer}>
          <CenteredImage src={imgSrc} width={IMGWIDTH} height={TOPHEIGHT} extraImgProps={{ isBlankCard }} />
          {cardColumns.length > 0 && (
            <div data-qa-component="CardColumns" className={styles.column}>
              {cardColumns}
            </div>
          )}
        </div>
        <div className={styles.infoRow}>
          <div className={styles.stars}>
            {!isNil(stars) && (
              <StarRatings
                rating={stars}
                numberOfStars={5}
                starDimension={starDimension}
                starSpacing={starSpacing}
                starRatedColor={starGoldColor}
              />
            )}
          </div>
          <div className={styles.value}>
            <ClippedTooltip content={renderedValue} className={styles.clipText} />
          </div>
        </div>
        <div className={styles.nameDescriptionContainer}>
          <div className={styles.description}>
            <ClippedTooltip content={renderedName} className={styles.clipText} />
          </div>
          <div className={styles.description}>
            <ClippedTooltip content={renderedDescription} className={styles.clampText} />
          </div>
        </div>
      </div>
    );
  }
}
